import './stylesVehiclesInfoTabs.less';

import Box from '@components/Box';
import type { EnrichedSidebarData } from '@features/sidebar';
import { FormattedOperationDays, TabType } from '@pages/driverAnalysis/DriverSidebar';
import { Spinner } from '@rio-cloud/rio-uikit';
import DataTab from '@rio-cloud/rio-uikit/DataTab';
import DataTabs from '@rio-cloud/rio-uikit/DataTabs';
import {
    combustionColumnNames,
    commonColumnNames,
    electricColumnNames,
    getColumns,
    hydrateColumns,
} from '@utils/columns';
import { convertToRows } from '@utils/convertToRows';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Column as ColumnType } from 'src/columns/createColumn';

import { Attribute, DateRange, HydratedEntity } from '../types';
import Column from './Column';

interface rowsData {
    dateRange: DateRange;
    tabKey: TabType;
    setTabKey: React.Dispatch<React.SetStateAction<TabType>>;
    data: EnrichedSidebarData;
    shouldUseV3Ratings: boolean;
    shouldUseV2Ratings: boolean;
    shouldShowHarshKPIs: boolean;
    shouldShowExcessiveKPIs: boolean;
    attributes?: Attribute[];
}
const convertToRow = convertToRows(3);
const VehiclesInfoTabs = ({ data, tabKey, setTabKey, attributes = [] }: rowsData) => {
    const commonColumns = hydrateColumns(getColumns(commonColumnNames), attributes);
    const combustionColumns = hydrateColumns(getColumns(combustionColumnNames), attributes).concat(
        commonColumns,
    );
    const electricColumns = hydrateColumns(getColumns(electricColumnNames), attributes).concat(
        commonColumns,
    );

    const displayedCombustionVehicleColumns = combustionColumns
        .map((column: ColumnType) => (
            <Column key={column.key} column={column} entity={data as HydratedEntity} />
        ))
        .concat([<FormattedOperationDays key="operationDays" entity={data as HydratedEntity} />]);

    const displayedElectricVehicleColumns = electricColumns
        .map((column) => (
            <Column key={column.key} column={column} entity={data as HydratedEntity} />
        ))
        .concat([<FormattedOperationDays key="operationDays" entity={data as HydratedEntity} />]);

    const vehicleCombustionRows = convertToRow(displayedCombustionVehicleColumns);
    const vehicleElectricRows = convertToRow(displayedElectricVehicleColumns);

    const tabsData = [
        {
            tabKey: TabType.FUEL_TAB,
            title: { text: 'fuelVehicles.tab', value: 'filling-station' },
            content: vehicleCombustionRows,
        },
        {
            tabKey: TabType.ELECTRIC_TAB,
            title: { text: 'electricVehicles.tab', value: 'flash' },
            content: vehicleElectricRows,
        },
    ];

    const getTabActiveClassName = (activeTab: string) => {
        const baseClass =
            'bg-white opacity-100 margin-bottom--1 margin-top-1 padding-y-5 rounded-tl-md rounded-tr-md rounded-top-large border';

        if (activeTab === TabType.FUEL_TAB) {
            return `${baseClass} border-right-none`;
        }
        if (activeTab === TabType.ELECTRIC_TAB) {
            return `${baseClass} border-left-none`;
        }

        return baseClass;
    };

    const tabClass =
        'bg-lighter opacity-50 height-70 display-flex align-items-center justify-content-center rounded-top-large rounded-bottom-large border';

    return (
        <div className="tabs-container">
            <DataTabs
                activeKey={tabKey}
                bordered={false}
                disableTransition={false}
                onSelectTab={(newTabKey) => setTabKey(newTabKey as TabType)}
                tabsWrapperClassName="bg-white padding-0"
                tabClassName={tabClass}
                tabHoverClassName={tabClass}
                tabActiveClassName={getTabActiveClassName(tabKey)}
                tabContentClassName="padding-0 padding-bottom-10"
                arrowClassName="d-none"
            >
                {tabsData.map((tab) => (
                    <DataTab
                        className="padding-0 border-bottom-none "
                        key={tab.tabKey}
                        tabKey={tab.tabKey}
                        data-test={'data-tab-sidebar-' + tab.tabKey}
                        title={<Title text={tab.title.text} value={tab.title.value} />}
                    >
                        <div className="margin-10 margin-top-0">
                            <div className="display-flex text-size-18 flex-row align-items-center">
                                <span className="margin-right-10">
                                    <span className="rioglyph rioglyph-tachograph"></span>
                                </span>
                                <span className="text-size-18">
                                    <FormattedMessage id="operatingData.label" />
                                </span>
                            </div>
                            {data.id ? <Box rows={tab.content} hasBorder={true} /> : <Spinner />}
                        </div>
                    </DataTab>
                ))}
            </DataTabs>
        </div>
    );
};

export default VehiclesInfoTabs;

export const Title = ({ text, value }: { text: string; value: React.ReactNode }) => (
    <div className="text-size-18 display-flex align-items-center justify-content-center height-60">
        <span className={`rioglyph rioglyph-${value}`} />
        <div className="padding-left-10">
            <FormattedMessage id={text} />
        </div>
    </div>
);
